import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ExpressAccount } from "../../types/express-master.type";
import { formatNumber } from "../../utils";
import MasterPicker from "../ExpressMaster/MasterPicker";
import { searchActiveAccounts } from "../ExpressMaster/masterSearch";
import CellTextField from "./CellTextField";
import { MONEY_DECIMALS } from "./constants";
import { DocumentDraft, PaymentLineDraft, newPaymentLine, sanitizeDecimal } from "./documentDraft";
import { DocumentFormErrors } from "./documentRules";
import { gridHeadCellSx, gridHeadSx } from "./gridStyles";
import { ExpressPaymentTotals } from "./totals";

type PaymentPatch = Partial<Pick<DocumentDraft, "cashAmount" | "whtAmount" | "paymentLines">>;

interface PaymentBlockProps {
  cashAmount: string;
  whtAmount: string;
  paymentLines: PaymentLineDraft[];
  paymentTotals: ExpressPaymentTotals;
  readOnly: boolean;
  errors: Pick<DocumentFormErrors, "paymentLines" | "received">;
  onChange: (patch: PaymentPatch) => void;
}

const ACCOUNT_PICKER_MIN_WIDTH = 240;
const AMOUNT_WIDTH = 140;
const CHEQUE_DATE_WIDTH = 170;
const NUMERIC_INPUT = { textAlign: "right" } as const;

function PaymentLineRow({
  line,
  readOnly,
  onUpdate,
  onRemove,
}: Readonly<{
  line: PaymentLineDraft;
  readOnly: boolean;
  onUpdate: (line: PaymentLineDraft) => void;
  onRemove: (key: string) => void;
}>) {
  const handleAccountChange = (account: ExpressAccount | null) =>
    onUpdate({ ...line, account: account ? { code: account.code, name: account.name } : null });
  const handleChequeDateChange = (chequeDate: Dayjs | null) => onUpdate({ ...line, chequeDate });
  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) =>
    onUpdate({ ...line, amount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleRemove = () => onRemove(line.key);
  const accountText = line.account ? `${line.account.code} ${line.account.name}` : "";

  return (
    <TableRow>
      <TableCell>
        {readOnly ? (
          <Typography variant="body2">{accountText}</Typography>
        ) : (
          <MasterPicker
            value={line.account}
            search={searchActiveAccounts}
            onChange={handleAccountChange}
            placeholder="ค้นหารหัสบัญชี"
            minWidth={ACCOUNT_PICKER_MIN_WIDTH}
          />
        )}
      </TableCell>
      <TableCell>
        <DatePicker
          value={line.chequeDate}
          format="DD/MM/YYYY"
          readOnly={readOnly}
          onChange={handleChequeDateChange}
          slotProps={{
            textField: { size: "small", sx: { width: CHEQUE_DATE_WIDTH } },
            field: { clearable: !readOnly },
          }}
        />
      </TableCell>
      <TableCell align="right">
        <CellTextField
          value={line.amount}
          readOnly={readOnly}
          displayValue={formatNumber(line.amount)}
          ariaLabel="จำนวนเงินที่ชำระโดยอื่นๆ"
          isNumeric
          width={AMOUNT_WIDTH}
          onChange={handleAmountChange}
        />
      </TableCell>
      {!readOnly && (
        <TableCell align="center">
          <Tooltip title="ลบรายการ">
            <IconButton size="small" onClick={handleRemove} aria-label="ลบรายการชำระโดยอื่นๆ">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}

function AmountField({
  label,
  value,
  readOnly,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  readOnly: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}>) {
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      placeholder="0.00"
      onChange={onChange}
      slotProps={{
        input: { readOnly },
        htmlInput: { inputMode: "decimal", style: NUMERIC_INPUT },
      }}
    />
  );
}

/**
 * How an O1 / E1 was received: cash, other payments (a bank account per line) and withholding
 * tax. ยอดชำระจริง adds them up and has to settle the document.
 */
export default function PaymentBlock({
  cashAmount,
  whtAmount,
  paymentLines,
  paymentTotals,
  readOnly,
  errors,
  onChange,
}: Readonly<PaymentBlockProps>) {
  const handleCashChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ cashAmount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleWhtChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ whtAmount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleUpdateLine = (updated: PaymentLineDraft) =>
    onChange({ paymentLines: paymentLines.map((line) => (line.key === updated.key ? updated : line)) });
  const handleRemoveLine = (key: string) =>
    onChange({ paymentLines: paymentLines.filter((line) => line.key !== key) });
  const handleAddLine = () => onChange({ paymentLines: [...paymentLines, newPaymentLine()] });

  // A viewed document shows its stored figures; a keyed one shows what is typed.
  const cashText = readOnly ? formatNumber(cashAmount || 0) : cashAmount;
  const whtText = readOnly ? formatNumber(whtAmount || 0) : whtAmount;

  return (
    <Stack gap={2}>
      <Typography variant="subtitle1" fontWeight={700}>
        การรับชำระ
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AmountField label="เงินสด" value={cashText} readOnly={readOnly} onChange={handleCashChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AmountField
            label="ชำระโดยอื่นๆ (ด้านล่าง)"
            value={formatNumber(paymentTotals.otherPaymentAmount)}
            readOnly
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AmountField label="ภาษี ณ ที่จ่าย" value={whtText} readOnly={readOnly} onChange={handleWhtChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AmountField label="ยอดชำระจริง" value={formatNumber(paymentTotals.receivedAmount)} readOnly />
        </Grid>
      </Grid>
      {errors.received && <Alert severity="error">{errors.received}</Alert>}
      {errors.paymentLines && <Alert severity="error">{errors.paymentLines}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead sx={gridHeadSx}>
            <TableRow>
              <TableCell sx={gridHeadCellSx}>ชำระโดยอื่นๆ</TableCell>
              <TableCell sx={gridHeadCellSx}>ลงวันที่</TableCell>
              <TableCell sx={gridHeadCellSx} align="right">
                จำนวนเงิน
              </TableCell>
              {!readOnly && <TableCell width={56} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentLines.map((line) => (
              <PaymentLineRow
                key={line.key}
                line={line}
                readOnly={readOnly}
                onUpdate={handleUpdateLine}
                onRemove={handleRemoveLine}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {paymentLines.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          ไม่มีรายการชำระโดยอื่นๆ
        </Typography>
      )}
      {!readOnly && (
        <Stack direction="row">
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddLine}>
            เพิ่มรายการชำระโดยอื่นๆ
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
